import { PieceClass, MovementPattern, PieceRules } from "../types/Board";

export interface PieceDefinition {
  type: string;
  name: string;
  abbreviation: string;
  class: PieceClass;
  index: [number, number];
  rules: PieceRules;
  baseValue: number;
  description: string;
}

// Movement pattern helpers for unlimited moves
const orthogonalUnlimited: MovementPattern = {
  type: "slide",
  directions: [
    { rowDelta: 1, colDelta: 0 },
    { rowDelta: -1, colDelta: 0 },
    { rowDelta: 0, colDelta: 1 },
    { rowDelta: 0, colDelta: -1 },
  ],
  maxDistance: "unlimited",
};

const diagonalUnlimited: MovementPattern = {
  type: "slide",
  directions: [
    { rowDelta: 1, colDelta: 1 },
    { rowDelta: 1, colDelta: -1 },
    { rowDelta: -1, colDelta: 1 },
    { rowDelta: -1, colDelta: -1 },
  ],
  maxDistance: "unlimited",
};

const knightMove: MovementPattern = {
  type: "jump",
  directions: [
    { rowDelta: 2, colDelta: 1 },
    { rowDelta: 2, colDelta: -1 },
    { rowDelta: -2, colDelta: 1 },
    { rowDelta: -2, colDelta: -1 },
    { rowDelta: 1, colDelta: 2 },
    { rowDelta: 1, colDelta: -2 },
    { rowDelta: -1, colDelta: 2 },
    { rowDelta: -1, colDelta: -2 },
  ],
  maxDistance: 1,
};

// All 36 Expanded Chess Pieces
export const PIECE_DEFINITIONS: Record<string, PieceDefinition> = {
  // RULING CLASS [6,6] to [6,1]
  empress: {
    type: "empress",
    name: "Empress",
    abbreviation: "EPS",
    class: "ruling",
    index: [6, 6],
    rules: {
      movements: [
        orthogonalUnlimited,
        diagonalUnlimited,
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 2 },
            { rowDelta: 3, colDelta: -2 },
            { rowDelta: -3, colDelta: 2 },
            { rowDelta: -3, colDelta: -2 },
            { rowDelta: 2, colDelta: 3 },
            { rowDelta: 2, colDelta: -3 },
            { rowDelta: -2, colDelta: 3 },
            { rowDelta: -2, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 12.31,
    description: "Combines Castle, Bishop, and Guard (3,2 L-Type) movements.",
  },

  chancellor: {
    type: "chancellor",
    name: "Chancellor",
    abbreviation: "CHL",
    class: "ruling",
    index: [6, 5],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
            { rowDelta: -1, colDelta: 1 },
            { rowDelta: -1, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 1 },
            { rowDelta: 3, colDelta: -1 },
            { rowDelta: -3, colDelta: 1 },
            { rowDelta: -3, colDelta: -1 },
            { rowDelta: 1, colDelta: 3 },
            { rowDelta: 1, colDelta: -3 },
            { rowDelta: -1, colDelta: 3 },
            { rowDelta: -1, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 11.81,
    description: "Combines Armory, Priest, and Archer (3,1 L-Type) movements.",
  },

  emperor: {
    type: "emperor",
    name: "Emperor",
    abbreviation: "EPR",
    class: "ruling",
    index: [6, 4],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 2,
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
            { rowDelta: -1, colDelta: 1 },
            { rowDelta: -1, colDelta: -1 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 5.19,
    description:
      "King with Abbey movement overlaid. Checkmate piece for larger boards.",
  },

  queen: {
    type: "queen",
    name: "Queen",
    abbreviation: "QUN",
    class: "ruling",
    index: [6, 3],
    rules: {
      movements: [orthogonalUnlimited, diagonalUnlimited],
      capturePattern: "same",
    },
    baseValue: 9.0,
    description:
      "Traditional chess Queen. Combines Castle and Bishop movements.",
  },

  duke: {
    type: "duke",
    name: "Duke",
    abbreviation: "DUK",
    class: "ruling",
    index: [6, 2],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
            { rowDelta: -1, colDelta: 1 },
            { rowDelta: -1, colDelta: -1 },
          ],
          maxDistance: 3,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 8.47,
    description: "Combines Armory and Priest movements.",
  },

  king: {
    type: "king",
    name: "King",
    abbreviation: "KNG",
    class: "ruling",
    index: [6, 1],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
            { rowDelta: -1, colDelta: 1 },
            { rowDelta: -1, colDelta: -1 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 3.15,
    description: "Traditional chess King. Checkmate piece.",
  },

  // ARCHITECTURAL CLASS [5,6] to [5,1]
  fortress: {
    type: "fortress",
    name: "Fortress",
    abbreviation: "FTS",
    class: "architectural",
    index: [5, 6],
    rules: {
      movements: [
        orthogonalUnlimited,
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 2 },
            { rowDelta: 3, colDelta: -2 },
            { rowDelta: -3, colDelta: 2 },
            { rowDelta: -3, colDelta: -2 },
            { rowDelta: 2, colDelta: 3 },
            { rowDelta: 2, colDelta: -3 },
            { rowDelta: -2, colDelta: 3 },
            { rowDelta: -2, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 9.56,
    description: "Combines Castle and Guard (3,2 L-Type) movements.",
  },

  citadel: {
    type: "citadel",
    name: "Citadel",
    abbreviation: "CDL",
    class: "architectural",
    index: [5, 5],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 1 },
            { rowDelta: 3, colDelta: -1 },
            { rowDelta: -3, colDelta: 1 },
            { rowDelta: -3, colDelta: -1 },
            { rowDelta: 1, colDelta: 3 },
            { rowDelta: 1, colDelta: -3 },
            { rowDelta: -1, colDelta: 3 },
            { rowDelta: -1, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 8.88,
    description: "Combines Armory and Archer (3,1 L-Type) movements.",
  },

  cathedral: {
    type: "cathedral",
    name: "Cathedral",
    abbreviation: "CTL",
    class: "architectural",
    index: [5, 4],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 2,
        },
        knightMove,
      ],
      capturePattern: "same",
    },
    baseValue: 7.07,
    description: "Combines Abbey and Knight movements.",
  },

  castle: {
    type: "castle",
    name: "Castle",
    abbreviation: "CSL",
    class: "architectural",
    index: [5, 3],
    rules: {
      movements: [orthogonalUnlimited],
      capturePattern: "same",
    },
    baseValue: 6.0,
    description: "Traditional chess Rook. Unlimited orthogonal movement.",
  },

  armory: {
    type: "armory",
    name: "Armory",
    abbreviation: "ARM",
    class: "architectural",
    index: [5, 2],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 3,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 5.55,
    description: "Orthogonal movement up to 3 squares.",
  },

  abbey: {
    type: "abbey",
    name: "Abbey",
    abbreviation: "ABY",
    class: "architectural",
    index: [5, 1],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 2,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 4.07,
    description: "Orthogonal movement up to 2 squares.",
  },

  // RELIGIOUS CLASS [4,6] to [4,1]
  pope: {
    type: "pope",
    name: "Pope",
    abbreviation: "POP",
    class: "religious",
    index: [4, 6],
    rules: {
      movements: [
        diagonalUnlimited,
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 2 },
            { rowDelta: 3, colDelta: -2 },
            { rowDelta: -3, colDelta: 2 },
            { rowDelta: -3, colDelta: -2 },
            { rowDelta: 2, colDelta: 3 },
            { rowDelta: 2, colDelta: -3 },
            { rowDelta: -2, colDelta: 3 },
            { rowDelta: -2, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 6.56,
    description: "Combines Bishop and Guard (3,2 L-Type) movements.",
  },

  archbishop: {
    type: "archbishop",
    name: "Archbishop",
    abbreviation: "ARB",
    class: "religious",
    index: [4, 5],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
            { rowDelta: -1, colDelta: 1 },
            { rowDelta: -1, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 1 },
            { rowDelta: 3, colDelta: -1 },
            { rowDelta: -3, colDelta: 1 },
            { rowDelta: -3, colDelta: -1 },
            { rowDelta: 1, colDelta: 3 },
            { rowDelta: 1, colDelta: -3 },
            { rowDelta: -1, colDelta: 3 },
            { rowDelta: -1, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 6.26,
    description: "Combines Priest and Archer (3,1 L-Type) movements.",
  },

  cardinal: {
    type: "cardinal",
    name: "Cardinal",
    abbreviation: "CRD",
    class: "religious",
    index: [4, 4],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
            { rowDelta: -1, colDelta: 1 },
            { rowDelta: -1, colDelta: -1 },
          ],
          maxDistance: 2,
        },
        knightMove,
      ],
      capturePattern: "same",
    },
    baseValue: 4.67,
    description: "Combines Nun and Knight movements.",
  },

  bishop: {
    type: "bishop",
    name: "Bishop",
    abbreviation: "BSP",
    class: "religious",
    index: [4, 3],
    rules: {
      movements: [diagonalUnlimited],
      capturePattern: "same",
    },
    baseValue: 3.0,
    description: "Traditional chess Bishop. Unlimited diagonal movement.",
  },

  priest: {
    type: "priest",
    name: "Priest",
    abbreviation: "PRS",
    class: "religious",
    index: [4, 2],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
            { rowDelta: -1, colDelta: 1 },
            { rowDelta: -1, colDelta: -1 },
          ],
          maxDistance: 3,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 2.93,
    description: "Diagonal movement up to 3 squares.",
  },

  nun: {
    type: "nun",
    name: "Nun",
    abbreviation: "NUN",
    class: "religious",
    index: [4, 1],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
            { rowDelta: -1, colDelta: 1 },
            { rowDelta: -1, colDelta: -1 },
          ],
          maxDistance: 2,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 1.67,
    description: "Diagonal movement up to 2 squares.",
  },

  // MILITARY CLASS [3,6] to [3,1]
  marshall: {
    type: "marshall",
    name: "Marshall",
    abbreviation: "MSL",
    class: "military",
    index: [3, 6],
    rules: {
      movements: [
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 2 },
            { rowDelta: 3, colDelta: -2 },
            { rowDelta: -3, colDelta: 2 },
            { rowDelta: -3, colDelta: -2 },
            { rowDelta: 2, colDelta: 3 },
            { rowDelta: 2, colDelta: -3 },
            { rowDelta: -2, colDelta: 3 },
            { rowDelta: -2, colDelta: -3 },
            { rowDelta: 3, colDelta: 1 },
            { rowDelta: 3, colDelta: -1 },
            { rowDelta: -3, colDelta: 1 },
            { rowDelta: -3, colDelta: -1 },
            { rowDelta: 1, colDelta: 3 },
            { rowDelta: 1, colDelta: -3 },
            { rowDelta: -1, colDelta: 3 },
            { rowDelta: -1, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 6.9,
    description:
      "Combines Guard (3,2 L-Type) and Archer (3,1 L-Type) movements.",
  },

  captain: {
    type: "captain",
    name: "Captain",
    abbreviation: "CPN",
    class: "military",
    index: [3, 5],
    rules: {
      movements: [
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 2 },
            { rowDelta: 3, colDelta: -2 },
            { rowDelta: -3, colDelta: 2 },
            { rowDelta: -3, colDelta: -2 },
            { rowDelta: 2, colDelta: 3 },
            { rowDelta: 2, colDelta: -3 },
            { rowDelta: -2, colDelta: 3 },
            { rowDelta: -2, colDelta: -3 },
          ],
          maxDistance: 1,
        },
        knightMove,
      ],
      capturePattern: "same",
    },
    baseValue: 6.56,
    description: "Combines Guard (3,2 L-Type) and Knight movements.",
  },

  sergeant: {
    type: "sergeant",
    name: "Sergeant",
    abbreviation: "SGT",
    class: "military",
    index: [3, 4],
    rules: {
      movements: [
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 1 },
            { rowDelta: 3, colDelta: -1 },
            { rowDelta: -3, colDelta: 1 },
            { rowDelta: -3, colDelta: -1 },
            { rowDelta: 1, colDelta: 3 },
            { rowDelta: 1, colDelta: -3 },
            { rowDelta: -1, colDelta: 3 },
            { rowDelta: -1, colDelta: -3 },
          ],
          maxDistance: 1,
        },
        knightMove,
      ],
      capturePattern: "same",
    },
    baseValue: 6.34,
    description: "Combines Archer (3,1 L-Type) and Knight movements.",
  },

  guard: {
    type: "guard",
    name: "Guard",
    abbreviation: "GRD",
    class: "military",
    index: [3, 3],
    rules: {
      movements: [
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 2 },
            { rowDelta: 3, colDelta: -2 },
            { rowDelta: -3, colDelta: 2 },
            { rowDelta: -3, colDelta: -2 },
            { rowDelta: 2, colDelta: 3 },
            { rowDelta: 2, colDelta: -3 },
            { rowDelta: -2, colDelta: 3 },
            { rowDelta: -2, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 3.56,
    description: "Extended L-Type movement (3,2 pattern).",
  },

  archer: {
    type: "archer",
    name: "Archer",
    abbreviation: "ARC",
    class: "military",
    index: [3, 2],
    rules: {
      movements: [
        {
          type: "jump",
          directions: [
            { rowDelta: 3, colDelta: 1 },
            { rowDelta: 3, colDelta: -1 },
            { rowDelta: -3, colDelta: 1 },
            { rowDelta: -3, colDelta: -1 },
            { rowDelta: 1, colDelta: 3 },
            { rowDelta: 1, colDelta: -3 },
            { rowDelta: -1, colDelta: 3 },
            { rowDelta: -1, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 3.34,
    description: "Extended L-Type movement (3,1 pattern).",
  },

  knight: {
    type: "knight",
    name: "Knight",
    abbreviation: "KNT",
    class: "military",
    index: [3, 1],
    rules: {
      movements: [knightMove],
      capturePattern: "same",
    },
    baseValue: 3.0,
    description: "Traditional chess Knight. L-Type movement (2,1 pattern).",
  },

  // WEAPONRY CLASS [2,6] to [2,1]
  catapult: {
    type: "catapult",
    name: "Catapult",
    abbreviation: "CTP",
    class: "weaponry",
    index: [2, 6],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: "unlimited",
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: "unlimited",
        },
      ],
      capturePattern: "same",
    },
    baseValue: 6.12,
    description: "Unlimited forward orthogonal and diagonal movement.",
  },

  ram: {
    type: "ram",
    name: "Ram",
    abbreviation: "RAM",
    class: "weaponry",
    index: [2, 5],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: "unlimited",
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "jump",
          directions: [
            { rowDelta: -3, colDelta: 1 },
            { rowDelta: -3, colDelta: -1 },
            { rowDelta: -1, colDelta: 3 },
            { rowDelta: -1, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 5.73,
    description: "Forward movement with limited backward L-Type.",
  },

  mace: {
    type: "mace",
    name: "Mace",
    abbreviation: "MAC",
    class: "weaponry",
    index: [2, 4],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: 3,
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: 2,
        },
        {
          type: "jump",
          directions: [
            { rowDelta: -2, colDelta: 1 },
            { rowDelta: -2, colDelta: -1 },
            { rowDelta: -1, colDelta: 2 },
            { rowDelta: -1, colDelta: -2 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 4.66,
    description: "Limited forward movement with backward Knight movement.",
  },

  lance: {
    type: "lance",
    name: "Lance",
    abbreviation: "LNC",
    class: "weaponry",
    index: [2, 3],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: "unlimited",
        },
        {
          type: "jump",
          directions: [
            { rowDelta: -3, colDelta: 2 },
            { rowDelta: -3, colDelta: -2 },
            { rowDelta: -2, colDelta: 3 },
            { rowDelta: -2, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 4.17,
    description: "Unlimited forward orthogonal with backward Guard movement.",
  },

  hatchet: {
    type: "hatchet",
    name: "Hatchet",
    abbreviation: "HCT",
    class: "weaponry",
    index: [2, 2],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: 3,
        },
        {
          type: "jump",
          directions: [
            { rowDelta: -3, colDelta: 1 },
            { rowDelta: -3, colDelta: -1 },
            { rowDelta: -1, colDelta: 3 },
            { rowDelta: -1, colDelta: -3 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 3.72,
    description: "Limited forward orthogonal with backward Archer movement.",
  },

  sword: {
    type: "sword",
    name: "Sword",
    abbreviation: "SWD",
    class: "weaponry",
    index: [2, 1],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: 2,
        },
        {
          type: "jump",
          directions: [
            { rowDelta: -2, colDelta: 1 },
            { rowDelta: -2, colDelta: -1 },
            { rowDelta: -1, colDelta: 2 },
            { rowDelta: -1, colDelta: -2 },
          ],
          maxDistance: 1,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 2.87,
    description: "Limited forward orthogonal with backward Knight movement.",
  },

  // SPECIAL CHARACTERS [1,6] to [1,1]
  angel: {
    type: "angel",
    name: "Angel",
    abbreviation: "AGL",
    class: "special",
    index: [1, 6],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: "unlimited",
        },
        {
          type: "slide",
          directions: [
            { rowDelta: -1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: "unlimited",
        },
      ],
      capturePattern: "same",
    },
    baseValue: 6.69,
    description: "Unlimited forward movement with limited backward/sideward.",
  },

  saint: {
    type: "saint",
    name: "Saint",
    abbreviation: "SNT",
    class: "special",
    index: [1, 5],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "slide",
          directions: [{ rowDelta: -1, colDelta: 0 }],
          maxDistance: 2,
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: "unlimited",
        },
      ],
      capturePattern: "same",
    },
    baseValue: 6.01,
    description: "Mixed limited orthogonal with unlimited forward diagonal.",
  },

  wizard: {
    type: "wizard",
    name: "Wizard",
    abbreviation: "WZD",
    class: "special",
    index: [1, 4],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 0 },
            { rowDelta: 0, colDelta: 1 },
            { rowDelta: 0, colDelta: -1 },
          ],
          maxDistance: 3,
        },
        {
          type: "slide",
          directions: [{ rowDelta: -1, colDelta: 0 }],
          maxDistance: 1,
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: 3,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 5.73,
    description: "Limited orthogonal and forward diagonal movement.",
  },

  squire: {
    type: "squire",
    name: "Squire",
    abbreviation: "SQR",
    class: "special",
    index: [1, 3],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: 3,
          conditions: ["move_only"],
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: 3,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 3.72,
    description:
      "Forward-only piece. Moves forward 3, moves/captures diagonally forward 3. Promotable.",
  },

  knave: {
    type: "knave",
    name: "Knave",
    abbreviation: "KNV",
    class: "special",
    index: [1, 2],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: 2,
          conditions: ["move_only"],
        },
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: 2,
        },
      ],
      capturePattern: "same",
    },
    baseValue: 2.48,
    description:
      "Forward-only piece. Moves forward 2, moves/captures diagonally forward 2. Promotable.",
  },

  pawn: {
    type: "pawn",
    name: "Pawn",
    abbreviation: "PWN",
    class: "special",
    index: [1, 1],
    rules: {
      movements: [
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: 1,
          conditions: ["move_only"],
        },
        {
          type: "slide",
          directions: [{ rowDelta: 1, colDelta: 0 }],
          maxDistance: 2,
          conditions: ["first_move", "move_only"],
        },
      ],
      capturePattern: [
        {
          type: "slide",
          directions: [
            { rowDelta: 1, colDelta: 1 },
            { rowDelta: 1, colDelta: -1 },
          ],
          maxDistance: 1,
          conditions: ["capture_only"],
        },
      ],
    },
    baseValue: 1.0,
    description:
      "Traditional chess Pawn. Forward-only, captures diagonally. Promotable.",
  },
};

// Helper functions
export function getPieceByAbbreviation(
  abbr: string
): PieceDefinition | undefined {
  return Object.values(PIECE_DEFINITIONS).find((p) => p.abbreviation === abbr);
}

export function getPiecesByClass(pieceClass: PieceClass): PieceDefinition[] {
  return Object.values(PIECE_DEFINITIONS).filter((p) => p.class === pieceClass);
}

export const TOTAL_PIECES = Object.keys(PIECE_DEFINITIONS).length;